import { Platform, Pressable, View } from "react-native"
import { useState, useDeferredValue } from "react"
import { useDebounce } from "use-debounce"

import { ActivityIndicator, Header, List, MCIcon, Text, TextInput } from "@src/components"
import { useNumCols } from "@src/hooks"
import { ar } from "@src/i18n/ar"
import { sharedStyles, trpc } from "@src/utils"
import { Link } from "expo-router"

export default function InventoryCollections() {
  const numCols = Math.max(useNumCols(260, { maxWidth: 1320 }), 2)
  const [search, setSearch] = useState("")
  const [debouncedSearch] = useDebounce(search, 500)
  const deferredSearch = useDeferredValue(debouncedSearch)
  const inventoryCollections = trpc.inventoryCollections.list.useInfiniteQuery({ search: deferredSearch.trim() }, { getNextPageParam: (lastPage) => lastPage.nextCursor })

  const header = (
    <Header
      title={ar.inventoryCollections}
      right={
        <View className="flex-row h-full items-center">
          {Platform.OS === "web" && (
            <Pressable className="px-2 h-full justify-center active:opacity-70" onPress={() => inventoryCollections.refetch()}>
              <MCIcon name="refresh" size={24} color="#60A8CD" />
            </Pressable>
          )}
          <Link href="/dashboard/inventory/collections/create" asChild>
            <Pressable className="px-2 h-full justify-center active:opacity-70">
              <MCIcon name="plus" size={24} color="#60A5FA" />
            </Pressable>
          </Link>
        </View>
      }
    />
  )

  return (
    <>
      {header}
      <List
        data={inventoryCollections.data?.pages.flatMap((page) => page.items) ?? []}
        numColumns={numCols}
        key={`inventory-collections-grid-${numCols}`}
        contentContainerStyle={sharedStyles.contentContainerWide}
        keyExtractor={(item) => item.id.toString()}
        ListHeaderComponent={
          <View className="w-full max-w-content mx-auto pt-1 pb-1 px-2">
            <TextInput placeholder={ar.searchInventoryCollections} value={search} onChangeText={setSearch} />
          </View>
        }
        onRefresh={inventoryCollections.refetch}
        onEndReachedThreshold={0.45}
        onEndReached={() => {
          if (inventoryCollections.hasNextPage && !inventoryCollections.isFetchingNextPage) {
            inventoryCollections.fetchNextPage()
          }
        }}
        ListEmptyComponent={
          inventoryCollections.isLoading ? (
            <ActivityIndicator className="py-8" />
          ) : (
            <View className="bg-white py-4 mt-4 rounded">
              <Text className="text-base font-bold text-slate-900 text-center">{ar.noInventoryCollections}</Text>
            </View>
          )
        }
        ListFooterComponent={inventoryCollections.isFetchingNextPage ? <ActivityIndicator className="pb-safe-offset-4" /> : <View className="pb-safe-offset-4" />}
        renderItem={({ item }) => (
          <Link href={`/dashboard/inventory/collections/${item.id}`} asChild>
            <Pressable className="m-2 p-2 bg-white rounded">
              <Text className="text-center font-bold text-base">{item.name}</Text>
            </Pressable>
          </Link>
        )}
      />
    </>
  )
}
